// INVARIANT: Every domain enum's tone mapping lives here, NEVER inline in a
// component file. The `ds-page-auditor` and `ds-primitive-auditor` agents
// both flag `Record<*, PillTone | ToneName>` definitions outside this file
// as violations. If you need a new tone map, add it here first; consumers
// import canonical, no exceptions.
//
// @ds/tokens/domainTones — domain enum → DS `ToneName` lookup tables.
//
// One row per domain enum (CompanyCategory, InvoiceDirection, GDStatus, …)
// mapped to a `ToneName` from `@ds/tokens/palette`. Consumers feed these
// directly into `<Tag tone={…}>`, `<Pill tone={…}>`, `col.statusTag({ colors })`,
// or any `TagSpec.colors` table — no AntD color strings, no hex codes.
//
// Migrated in 2026-05 from the legacy `*_COLORS` tables in
// `src/types/constants.ts`, which used AntD-flavoured strings
// ("magenta", "cyan", "gold", "volcano", …) that don't render against
// the DS palette.

import type { ToneName } from "./palette";
import type {
  CompanyCategory,
  BorderType,
  TransactionStatus,
  PriceType,
  TransportType,
  VatRateCode,
  ContactPurpose,
  EmployeeRole,
  GDStatus,
  DocumentType,
  DocumentStatus,
  ReviewStatus,
  CommentTag,
  InvoiceType,
  InvoiceDirection,
  InvoiceSource,
  InvoiceReviewStatus,
  InvoicePaymentStatus,
  InvoiceSyncStatus,
  PaymentMethod,
  CompanyDocumentType,
  InvoicedStatus,
  CommissionType,
  EmploymentType,
  EffectivePaymentState,
} from "@shared/types";


export const BRAND_COLORS = {
  googleDocs:       "#4285F4",
  googleSheets:     "#0F9D58",
  googleSlides:     "#F4B400",
  googleForms:      "#673AB7",
  googleFolder:     "#5F6368",
  msWord:           "#2b579a",
  msExcel:          "#217346",
  msPowerPoint:     "#d24726",
  textFile:         "#595959",
  archive:          "#722ed1",
} as const;

// ============================================
// COLOR MAPS — Record<DomainEnum, ToneName>
// ============================================

export const RECURRENCE_TONES: Record<string, ToneName> = {
  none:      "grey",
  daily:     "blue",
  weekly:    "teal",
  monthly:   "green",
  quarterly: "amber",
  yearly:    "purple",
};

export const BORDER_TYPE_COLORS: Record<BorderType, ToneName> = {
  domestic: "grey",
  eu:       "blue",
  non_eu:   "amber",
};

/**
 * One hue per category — the map legends all eight at once, so any two sharing
 * a tone read as one kind. Previously `waste_generator`/`organizacja_odzysku`
 * were both amber and `importer`/`commercial` both purple, which made those
 * pairs indistinguishable as pins AND as chips.
 *
 * `grey` is deliberately absent: it belongs to the map's cluster circles, and
 * a category wearing it would look like a collapsed group of companies.
 */
export const CATEGORY_COLORS: Record<CompanyCategory, ToneName> = {
  recycler:            "green",
  transporter:         "blue",
  waste_generator:     "orange",
  exporter:            "teal",
  importer:            "purple",
  organizacja_odzysku: "lime",
  commercial:          "pink",
  other:               "cyan",
};

export const PURPOSE_COLORS: Record<ContactPurpose, ToneName> = {
  transport_notification: "blue",
  on_annex_doc:           "green",
  sales:                  "amber",
  invoicing:              "purple",
  weight_confirmation:    "teal",
  edpr:                   "green",
};

export const ROLE_COLORS: Record<EmployeeRole, ToneName> = {
  admin:          "red",
  management:     "purple",
  sales:          "blue",
  administration: "amber",
  logistics:      "green",
  developer:      "grey",
};

export const GD_STATUS_COLORS: Record<GDStatus, ToneName> = {
  yes: "green",
  no:  "red",
  na:  "grey",
};

export const DOCUMENT_TYPE_COLORS: Record<DocumentType, ToneName> = {
  ie529:             "blue",
  ie599:             "teal",
  cmr:               "red",
  annex7:            "purple",
  weight_tickets:    "amber",
  transport_order:   "green",
  transport_invoice: "blue",
  waste_image:       "purple",
  other:             "grey",
};

export const DOCUMENT_STATUS_COLORS: Record<DocumentStatus, ToneName> = {
  original:  "green",
  copy:      "amber",
  generated: "blue",
};

export const REVIEW_STATUS_COLORS: Record<ReviewStatus, ToneName> = {
  pending:        "amber",
  approved:       "green",
  not_approved:   "red",
  not_applicable: "grey",
};

export const COMMENT_TAG_COLORS: Record<CommentTag, ToneName> = {
  cancellation_reason:    "red",
  price_change:           "amber",
  system_note:            "blue",
  transport_instructions: "green",
};

export const PRICE_TYPE_COLORS: Record<PriceType, ToneName> = {
  per_ton:   "teal",
  flat_rate: "grey",
};

export const TRANSPORT_TYPE_COLORS: Record<TransportType, ToneName> = {
  truck:     "blue",
  train:     "teal",
  ship:      "purple",
  spedition: "amber",
};

export const INVOICE_TYPE_COLORS: Record<InvoiceType, ToneName> = {
  original:      "blue",
  correction:    "amber",
  proforma:      "teal",
  podatek:       "red",
  zus:           "purple",
  wynagrodzenie: "grey",
};

export const INVOICE_DIRECTION_COLORS: Record<InvoiceDirection, ToneName> = {
  issued:   "green",
  received: "red",
};

export const INVOICE_SOURCE_COLORS: Record<InvoiceSource, ToneName> = {
  fakturownia: "blue",
  ksef:        "purple",
  manual:      "grey",
};

export const INVOICE_REVIEW_STATUS_COLORS: Record<InvoiceReviewStatus, ToneName> = {
  pending:             "amber",
  approved:            "green",
  rejected:            "red",
  partial:             "red",
  request_correction:  "blue",
};

export const INVOICE_PAYMENT_STATUS_COLORS: Record<InvoicePaymentStatus, ToneName> = {
  paid:    "green",
  unpaid:  "red",
  partial: "amber",
  on_hold: "amber",
};

export const INVOICE_PAYMENT_METHOD_COLORS: Record<PaymentMethod, ToneName> = {
  transfer:     "blue",
  cash:         "green",
  card:         "purple",
  compensation: "amber",
  other:        "grey",
};

export const INVOICED_STATUS_COLORS: Record<InvoicedStatus, ToneName> = {
  none:    "grey",
  partial: "amber",
  full:    "green",
};

/**
 * Derived effective payment state (computed from `mbank_invoice_payment_state`).
 * Distinct from `INVOICE_PAYMENT_STATUS_COLORS` which colors the stored `payment_status` field.
 */
export const EFFECTIVE_PAYMENT_STATE_COLORS: Record<EffectivePaymentState, ToneName> = {
  unpaid:   "grey",
  partial:  "amber",
  paid:     "green",
  overpaid: "blue",
};

/**
 * Glyph identifiers (AntD icon component names) for non-color cues on effective
 * payment state — color-blind accessibility. `null` = no glyph. `on_hold` is an
 * overlay flag on stored status, not part of the effective taxonomy.
 */
export const EFFECTIVE_PAYMENT_STATE_GLYPHS: Record<EffectivePaymentState | "on_hold", string | null> = {
  unpaid:   null,
  partial:  "PieChartOutlined",
  paid:     "CheckOutlined",
  overpaid: "ArrowUpOutlined",
  on_hold:  "PauseCircleOutlined",
};

/**
 * PL VAT whitelist verification gating. Drives the `PartyCard` whitelist pill.
 * Keyed by string (the `AccountWhitelistStatus` literal union lives in
 * `@ds/layout/PartyCard` since it's PartyCard-specific surface).
 */
export const ACCOUNT_WHITELIST_STATUS_COLORS: Record<string, ToneName> = {
  verified:       "green",
  not_found:      "red",
  not_applicable: "grey",
};

/**
 * `company_bank_accounts.verification_status` trust ladder. Payable set
 * (payment_verified/operator_verified) reads green; whitelisted (MF-only,
 * never paid or human-confirmed) reads blue; unverified (learned from a
 * document, nothing more) reads amber; rejected/superseded are terminal
 * states (red/grey). Supersedes `ACCOUNT_WHITELIST_STATUS_COLORS` above for
 * the row-card pill on `company_bank_accounts` once the column is populated.
 */
export const BANK_ACCOUNT_VERIFICATION_STATUS_COLORS: Record<string, ToneName> = {
  payment_verified:  "green",
  operator_verified: "green",
  whitelisted:       "blue",
  unverified:        "amber",
  rejected:          "red",
  superseded:        "grey",
};

// mBank (Bank) outbound batch lifecycle.
export const BANK_BATCH_STATUS_COLORS: Record<string, ToneName> = {
  pending_send:      "amber",
  sent:              "blue",
  to_auth:           "amber",
  authorized:        "teal",
  in_realiz:         "blue",
  settled:           "green",
  partially_settled: "green",
  denied:            "red",
  cancelled:         "grey",
};

// mBank (Bank) outbound item statuses (same as batch minus partially_settled).
export const BANK_PAYMENT_STATUS_COLORS: Record<string, ToneName> = {
  pending_send: "amber",
  sent:         "blue",
  to_auth:      "amber",
  authorized:   "teal",
  in_realiz:    "blue",
  settled:      "green",
  denied:       "red",
  cancelled:    "grey",
};

// mBank (Bank) statement line match states.
export const BANK_LINE_MATCH_STATE_COLORS: Record<string, ToneName> = {
  pending:           "amber",
  matched:           "green",
  ambiguous:         "amber",
  manual:            "teal",
  partially_matched: "green",
};

// mBank (Bank) statement report format. Used by `BankReportFormatChip` on the
// statement list/detail. Two ingest families: ISO 20022 (camt) → blue, SWIFT MT
// (mt) → purple. Keeps the visual paired by protocol.
export const BANK_REPORT_FORMAT_COLORS: Record<string, ToneName> = {
  camt053: "blue",
  camt052: "blue",
  mt940:   "purple",
  mt942:   "purple",
};

// mBank (Bank) payment-link types — payment / refund / reversal / adjustment.
export const MBANK_LINK_TYPE_COLORS: Record<string, ToneName> = {
  payment:    "blue",
  refund:     "purple",
  reversal:   "red",
  adjustment: "amber",
};

// Fakturownia outbound push lifecycle for mbank_payment_links.
export const FAKTUROWNIA_PUSH_STATUS_COLORS: Record<string, ToneName> = {
  pushed:  "green",
  pending: "amber",
  failed:  "red",
  skipped: "grey",
};

// Fakturownia payment allocation reconcile state (fakturownia_payment_allocation.state)
// + the unified bank-payments ledger states (bank_payments_unified.state).
export const ALLOCATION_STATE_COLORS: Record<string, ToneName> = {
  pending:   "grey",
  echo:      "blue",
  linked:    "green",
  fa_only:   "teal",
  conflict:  "red",
  rejected:  "grey",
  // Unified ledger legs.
  synced:    "green",
  not_in_fa: "grey",
  orphan:    "amber",
  // Manual FA correction-netting, auto-resolved by the reconciler.
  settled_by_correction: "blue",
};

// Fakturownia payment settlement class (fakturownia_payment.settlement_class).
export const FA_SETTLEMENT_CLASS_COLORS: Record<string, ToneName> = {
  cash:                     "blue",
  compensation:             "purple",
  correction_consolidation: "amber",
  other:                    "grey",
};

// Interim (payments_invoice_linking=false) payment-level pairing status —
// bank_payments_pairing view + fakturownia_payment.pairing_status. `ambiguous`
// uses "amber" (chosen when the palette had no distinct orange; `orange` is a
// real tone now, so this is a candidate swap, not a constraint).
// `not_in_fa` is leg-2-only (uncovered bank lines);
// `pending` is the view's coalesce fallback for a not-yet-swept payment.
export const FA_PAIRING_STATUS_COLORS: Record<string, ToneName> = {
  paired:            "green",
  manual_exception:  "blue",
  pending:           "grey",
  no_line:           "red",
  ambiguous:         "amber",
  not_in_fa:         "red",
};

export const TRANSACTION_STATUS_COLORS: Record<TransactionStatus, ToneName> = {
  cancelled: "red",
};

export const INVOICE_SYNC_STATUS_COLORS: Record<InvoiceSyncStatus, ToneName> = {
  synced:     "green",
  conflict:   "amber",
  local_only: "blue",
  unlinked:   "grey",
};

export const COMPANY_DOCUMENT_TYPE_COLORS: Record<CompanyDocumentType, ToneName> = {
  green_contract:                   "green",
  recycler_decision:                "blue",
  collector_decision:               "teal",
  insurance:                        "purple",
  permit:                           "amber",
  recycling_percentage_certificate: "green",
  other:                            "grey",
};

export const COMMISSION_TYPE_COLORS: Record<CommissionType, ToneName> = {
  percentage: "blue",
  per_ton:    "teal",
  flat_rate:  "green",
};

export const EMPLOYMENT_TYPE_COLORS: Record<EmploymentType, ToneName> = {
  employment: "blue",
  b2b:        "green",
};

// Numeric rates ramp from neutral to amber-warm as the rate drops; alpha
// codes (ZW / NP) describe exemptions rather than a rate level.
export const VAT_RATE_COLORS: Record<VatRateCode, ToneName> = {
  "23": "blue",
  "8":  "teal",
  "5":  "amber",
  "0":  "grey",
  zw:   "purple",
  np:   "grey",
};

// Grouped by delivery-responsibility band (E/F/C/D).
export const INCOTERMS_COLORS: Record<string, ToneName> = {
  EXW: "grey",
  FCA: "blue",
  FAS: "blue",
  FOB: "blue",
  CFR: "teal",
  CIF: "teal",
  CPT: "teal",
  CIP: "teal",
  DAP: "green",
  DPU: "green",
  DDP: "green",
};

// Tri-state boolean: true → wnioskują, false → nie wnioskują, null → nie dotyczy.
export const DPR_STATE_COLORS: Record<"true" | "false" | "null", ToneName> = {
  true:  "green",
  false: "red",
  null:  "grey",
};

// BDO-scraped EDPR request statuses (Polish text from the portal).
export const EDPR_REQUEST_STATUS_COLORS: Record<string, ToneName> = {
  "Zaakceptowany": "green",
  "Złożony":       "blue",
  "Odrzucony":     "red",
  "Wycofany":      "grey",
};

// BDO-scraped EDPR document statuses (Polish text from the portal).
export const EDPR_DOCUMENT_STATUS_COLORS: Record<string, ToneName> = {
  "Wystawiony":    "blue",
  "Zaakceptowany": "green",
  "Odrzucony":     "red",
  "Wycofany":      "grey",
};

// ISO-4217 currency codes — stable per currency for cross-view recognition.
export const CURRENCY_COLORS: Record<string, ToneName> = {
  PLN: "blue",
  EUR: "purple",
  USD: "green",
};

// Load / unload side axis used by transactions, transports, and the SideChip.
// Note: price lists use a separate PRICE_LIST_SIDE_COLORS with unload→green.
export const SIDE_COLORS: Record<"load" | "unload", ToneName> = {
  load:   "blue",
  unload: "purple",
};

// Load / unload side axis specific to price-list display (EnumTag PriceListSideTag).
// Distinct from SIDE_COLORS: price-list side uses green for unload (editorial convention
// dating from the price-list table; changing it would require a product decision).
export const PRICE_LIST_SIDE_COLORS: Record<"load" | "unload", ToneName> = {
  load:   "blue",
  unload: "green",
};

// Price-list request (price_list_requests.status): new = waiting for
// administration, done = price list created.
export const PRICE_LIST_REQUEST_STATUS_COLORS: Record<"new" | "done", ToneName> = {
  new:  "blue",
  done: "green",
};

// Tri-value reconcile side: load / unload / transport.
// Adds "transport" (purple) on top of the standard load/unload pair.
export const RECONCILE_SIDE_COLORS: Record<"load" | "unload" | "transport", ToneName> = {
  load:      "blue",
  unload:    "green",
  transport: "purple",
};

// mBank (Bank) statement-line derived operation status.
export const OPERATION_STATUS_COLORS: Record<string, ToneName> = {
  auto:         "green",
  suggest:      "amber",
  pending:      "red",
  partial:      "amber",
  manual:       "purple",
  bank_fee:     "grey",
  own_transfer: "grey",
  tax_payment:  "grey",
  payroll:      "grey",
  reversal:     "red",
};

// mBank (Bank) payment method derived from operation code / card presence.
export const PAYMENT_METHOD_COLORS: Record<string, ToneName> = {
  transfer:    "blue",
  card:        "purple",
  card_refund: "purple",
  tax:         "amber",
  fx:          "teal",
  own:         "grey",
  fee:         "grey",
  unknown:     "grey",
};

// Document-pipeline per-row display status (the `display_status` column on the
// `pipeline_extractions` view — a precomputed job-aware roll-up).
export const PIPELINE_DISPLAY_STATUS_COLORS: Record<string, ToneName> = {
  processing: "blue",
  pending:    "amber",
  unmatched:  "red",
  approved:   "green",
  rejected:   "red",
  failed:     "red",
};

// Document-pipeline completeness verdict (`completeness_status` — whether the AI
// found every required field/mark on the scan).
export const PIPELINE_COMPLETENESS_COLORS: Record<string, ToneName> = {
  complete:      "green",
  missing_parts: "amber",
  illegible:     "red",
};
